import { z } from 'zod';
import { IsoTimestamp, SchoolId, UserId } from './common.js';
/**
 * Class Membership — date-effective user-to-classroom relationship.
 *
 * Each record represents one User's assignment to one Classroom with an
 * effective date range. When a student moves or is promoted, the old
 * membership is closed (effectiveTo + inactive) and a new one is created.
 * Historical records are never hard-deleted: retiring a membership ends it
 * with `effectiveTo` + `inactive`, and `archived` is the terminal state that
 * keeps it out of day-to-day lists while remaining part of the history.
 *
 * CBT supports both standalone mode (admin manages locally) and integrated
 * mode (synced from AIMSIS with external IDs for matching).
 *
 * Firestore path: schools/{schoolId}/classMemberships/{membershipId}
 */
export const ClassMembershipSource = z.enum(['local', 'integrated']);
export const ClassMembershipStatus = z.enum(['active', 'inactive', 'archived']);
/**
 * Effective dates are calendar dates, not instants: membership boundaries are
 * whole days, and `YYYY-MM-DD` sorts chronologically as a plain string, which
 * is what the Firestore `orderBy('effectiveFrom')` and the overlap comparison
 * both rely on.
 *
 * The format is enforced here rather than left to the callers because an empty
 * string is lexicographically before every date — an unvalidated `effectiveTo`
 * of `""` would silently end the interval before it starts and disable the
 * overlap guard for that row. The web editor uses `<input type="date">`, but
 * the API is a public contract and cannot assume that.
 */
export const EffectiveDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be a YYYY-MM-DD date');
export const ClassMembership = z.object({
    id: z.string().min(1).max(64),
    schoolId: SchoolId,
    userId: UserId,
    classroomId: z.string().min(1).max(128),
    effectiveFrom: EffectiveDate,
    effectiveTo: EffectiveDate.nullable().optional(),
    status: ClassMembershipStatus.default('active'),
    source: ClassMembershipSource.default('local'),
    externalMembershipId: z.string().min(1).max(128).nullable().optional(),
    externalUserId: z.string().min(1).max(128).nullable().optional(),
    externalClassroomId: z.string().min(1).max(128).nullable().optional(),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
});
export const CreateClassMembershipRequest = z.object({
    userId: z.string().min(1).max(128),
    classroomId: z.string().min(1).max(128),
    effectiveFrom: EffectiveDate,
    effectiveTo: EffectiveDate.nullable().optional(),
    status: ClassMembershipStatus.optional(),
});
export const UpdateClassMembershipRequest = z.object({
    userId: z.string().min(1).max(128).optional(),
    classroomId: z.string().min(1).max(128).optional(),
    effectiveFrom: EffectiveDate.optional(),
    effectiveTo: EffectiveDate.nullable().optional(),
    status: ClassMembershipStatus.optional(),
});
//# sourceMappingURL=class-membership.js.map